import { Box, Image } from "@chakra-ui/react";
import { customEvent, getCdnImageSrc, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";
import { useEffect, useState } from "react";

import type { ParlorOption } from "@/types/parlorOption";

import { MubinsoFixedCTA } from "@/containers/MatcRecommendationContainer/components/MubinsoFixedCTA";

import { MubinsoInfoBox, MubinsoScreenLayout } from "./MubinsoUI";

/**
 * 처음에 세울 개수. `MubinsoRegionParlorList`(#344)가 5행이라 같은 수로 맞춘다.
 * 시군구당 장례식장은 중앙값 4곳(최대 20곳)이라 대부분 더보기 없이 다 보인다.
 */
const MAX_ITEMS = 5;

const DEFAULT_IMAGE = getCdnImageSrc("platform/parlor/parlor-img-default-2.png");

/**
 * 하단 고정 버튼이 본문을 가리지 않게 비워두는 높이. #344 와 같은 값이다.
 *
 * 버튼 둘이 다 뜨는 최대치 기준이다(그라데이션 40 + 48 + 간격 10 + 48 + 아래 여백 20 = 166).
 * 고를수록 줄어드는 값으로 두면 체크할 때마다 본문이 위아래로 튄다.
 */
const FIXED_CTA_CLEARANCE = 170;

/**
 * 주소에서 시도명을 뗀다. 시트가 이미 한 지역으로 걸러져 있고 헤드라인에도 지역명이 있어
 * "서울특별시" 가 모든 줄 앞에 반복될 이유가 없다.
 *
 * 시군구까지 떼지 않는 이유는 세종처럼 시군구가 없는 주소에서 도로명이 잘리기 때문이다
 * (지역 비용 프레임 C안 바텀시트와 같은 규칙).
 */
const stripSido = (address: string): string => {
  const [head, ...rest] = address.split(" ");
  if (!rest.length || !/(시|도)$/.test(head)) return address;
  return rest.join(" ");
};

interface MubinsoParlorPickScreenProps {
  /** "경남 창원시" 처럼 완성된 지역명. 헤드라인과 안내 문구에 그대로 쓴다 */
  regionLabel: string;
  /** 이미 고른 시군구로 걸러진 목록. 비어 있으면 호출부가 이 화면을 아예 세우지 않는다 */
  parlors: ParlorOption[];
  selectedIds: number[];
  /**
   * 토글할 id 하나만 넘긴다. 바뀐 배열 전체를 넘기면 클릭 시점 props 로 계산돼
   * 빠르게 두 곳을 누를 때 앞 선택이 덮인다(지역 비용 프레임 C안에서 잡은 버그).
   * 부모가 함수형 setState 로 받는다.
   */
  onToggle: (id: number) => void;
  onNext: () => void;
}

/**
 * 관심 장례식장 체크 화면(무빈소 찾기 퍼널 B안).
 *
 * 왜 무빈소 가능 여부를 가르지 않는가
 * - 이 화면은 **가입 전**이다. 여기서 "여기는 가능" 을 먼저 보여주면 리드를 낼 이유가
 *   사라진다. 목록의 역할은 답을 주는 것이 아니라 관심을 받아내는 것이다.
 * - #344 는 가입 후 화면이라 `isMubinsoAvailableParlor` 로 갈라 보여준다. 그쪽은 약속한
 *   답을 이행하는 자리라 성격이 반대다.
 * - 판정 데이터가 있는 곳은 1,073곳 중 126곳(11.7%)뿐이라 전부 "확인 필요"로 적어도
 *   사실과 어긋나지 않는다.
 *
 * 비용을 넣지 않는 이유
 * - `expected_charge_sum.무빈소` 는 요금표 산술값이라 운영 실측과 어긋난다(#344 주석의
 *   안양메트로 계산 103만/실측 300만). 고르는 자리에서 비용까지 보여줄 이유도 없다.
 */
export default function MubinsoParlorPickScreen({
  regionLabel,
  parlors,
  selectedIds,
  onToggle,
  onNext,
}: MubinsoParlorPickScreenProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "pick-view" } });
  }, []);

  const visible = isExpanded ? parlors : parlors.slice(0, MAX_ITEMS);
  const hiddenCount = parlors.length - visible.length;
  const selectedCount = selectedIds.length;

  return (
    <MubinsoScreenLayout
      // 지역 선택 화면과 같은 선택 화면이라 진행바를 두지 않는다. 여기에만 바를 두면
      // B 배정자의 스텝 수가 A 와 달라 보인다.
      contentBottomGap={0}
    >
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {regionLabel}에서
        <br />
        궁금한 장례식장이 있으신가요?
      </Box>
      <Spacing gap={14} />
      <Box width="100%" className="body_16_r gray600">
        선택하신 장례식장의 무빈소 진행 가능 여부 및 비용을 확인하여 알려드릴게요. 다만 꼭 지금
        선택하지는 않으셔도 괜찮아요.
      </Box>
      <Spacing gap={24} />
      {/*
       * 안 고르고 넘어가도 된다는 말은 부제(위)에 있다. 이 박스는 "왜 확인이 필요한가" 한 가지만
       * 말한다. 두 말을 한 박스에 넣었더니 박스가 커져 장례식장 카드가 첫 화면에서 밀렸다.
       * 고르는 화면이라 목록이 먼저 보여야 한다.
       */}
      <MubinsoInfoBox>
        무빈소 가능 여부는 장례식장 사정에 따라서 달라지기 때문에 꼭 확인해보시는 것이 좋아요.
      </MubinsoInfoBox>
      <Spacing gap={24} />

      <BaseStack width="100%" spacing={10}>
        {visible.map((parlor) => {
          const isSelected = selectedIds.includes(parlor.id);
          return (
            <BaseStack
              key={parlor.id}
              direction="horizontal"
              width="100%"
              alignItems="center"
              spacing={14}
              padding="16px"
              borderRadius="14px"
              backgroundColor={isSelected ? baseColors.orange_50 : baseColors.white}
              border={`1.5px solid ${
                isSelected ? baseColors.primary_orange : baseColors.line_normal
              }`}
              cursor="pointer"
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => {
                customEvent({
                  newGtm: { path: "mubinso_funnel", trigger_and_target: "pick-answer" },
                });
                onToggle(parlor.id);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle(parlor.id);
                }
              }}
            >
              <Image
                src={parlor.imageUrl || DEFAULT_IMAGE}
                fallbackSrc={DEFAULT_IMAGE}
                alt=""
                width="56px"
                height="56px"
                flexShrink={0}
                borderRadius="10px"
                objectFit="cover"
              />

              <BaseStack flex={1} minW={0} spacing={6}>
                <Box className="label_2_sb" color={baseColors.label_assistive_1} fontWeight={700}>
                  확인 필요
                </Box>
                <Box
                  className="headline_3_sb"
                  color={baseColors.label_normal}
                  fontWeight={700}
                  wordBreak="keep-all"
                  lineHeight="1.35"
                >
                  {parlor.name}
                </Box>
                <Box
                  className="label_2_r"
                  color={baseColors.label_assistive_1}
                  wordBreak="keep-all"
                  lineHeight="1.45"
                >
                  {stripSido(parlor.address)}
                </Box>
              </BaseStack>

              {/*
               * 원형 체크. 체크 표시를 "✓" 문자가 아니라 SVG 로 그린다. U+2713 글리프가
               * Pretendard 에 없으면 시스템 폰트로 폴백해 iOS 에서 모양이 달라진다(#344 와 같은 이유).
               */}
              <Box
                flexShrink={0}
                width="22px"
                height="22px"
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border={`1.5px solid ${
                  isSelected ? baseColors.primary_orange : baseColors.line_normal
                }`}
                backgroundColor={isSelected ? baseColors.primary_orange : "transparent"}
              >
                {isSelected && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke={baseColors.white}
                    strokeWidth={2.6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M3.6 8.4 L6.5 11.3 L12.4 4.9" />
                  </svg>
                )}
              </Box>
            </BaseStack>
          );
        })}
      </BaseStack>

      {hiddenCount > 0 && (
        <>
          <Spacing gap={12} />
          <Box
            as="button"
            type="button"
            width="100%"
            padding="14px 0"
            borderRadius="12px"
            border={`1px solid ${baseColors.line_normal}`}
            backgroundColor={baseColors.white}
            className="body_14_m gray700"
            cursor="pointer"
            onClick={() => {
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "pick-more" },
              });
              setIsExpanded(true);
            }}
          >
            {hiddenCount}곳 더보기
          </Box>
        </>
      )}
      <Spacing gap={FIXED_CTA_CLEARANCE} />

      {/*
       * 목록 아래가 아니라 화면 아래에 고정한다. 장례식장이 20곳인 시군구(창원, 전주, 목포)에서
       * 더보기를 펼치면 인라인 버튼은 화면 밖으로 밀린다. 이 화면의 이탈은 "건너뛰기를 눌러서"가
       * 아니라 "무엇을 눌러야 할지 몰라 아무것도 안 누르고 나가는" 쪽이라 누를 것이 항상 보여야
       * 한다. #344 장례식장 선택 화면과 같은 컴포넌트를 쓴다.
       *
       * 건너뛰기도 같은 바에 둔다. 작은 글씨로 본문에 흘려두면 고를 것이 없는 사람이 다음 행동을
       * 못 찾는다. 고른 것이 있으면 건너뛸 이유가 없으므로 그때는 내린다.
       */}
      <MubinsoFixedCTA
        primary={{
          label:
            selectedCount === 0
              ? "궁금한 장례식장을 골라주세요"
              : `선택한 ${selectedCount}곳 확인하기`,
          disabled: selectedCount === 0,
          onClick: () => {
            customEvent({
              newGtm: { path: "mubinso_funnel", trigger_and_target: "pick-next" },
            });
            onNext();
          },
        }}
        secondary={
          selectedCount === 0
            ? {
                label: "아직 찾고 있는 곳이 없어요",
                onClick: () => {
                  customEvent({
                    newGtm: { path: "mubinso_funnel", trigger_and_target: "pick-skip" },
                  });
                  onNext();
                },
              }
            : undefined
        }
      />
    </MubinsoScreenLayout>
  );
}
